import { Moon, Sparkles, Sun } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { StatCard } from '@/components/app/StatCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/useToast';

/**
 * Dev-only smoke page for Slice A shadcn primitives.
 * Remove in Slice E cutover.
 */
export function ComponentsShowcase() {
  return (
    <div className="space-y-8">
      <PageHeader
        title="Component showcase"
        description="Slice A smoke test — verifies shadcn primitives render under the AlignMate brand tokens."
        actions={
          <Button variant="gradient" onClick={() => toast({ title: 'Hello', description: 'Toast system works.' })}>
            Fire toast
          </Button>
        }
      />

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Sends · 24h" value="12,431" delta={4.2} icon={Sparkles} />
        <StatCard label="Open rate" value="38.4%" delta={-1.1} tone="warning" />
        <StatCard label="Suppressions" value="91" hint="global cap" tone="success" />
        <StatCard label="p99 queue" value="423ms" tone="destructive" />
      </section>

      <section className="space-y-4">
        <h2 className="font-heading text-lg font-semibold">Buttons</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Button>Default</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="gradient">Gradient</Button>
          <Button variant="destructive">Destructive</Button>
          <Button size="sm">Small</Button>
          <Button size="lg">Large pill</Button>
          <Button size="icon" aria-label="icon">
            <Sparkles />
          </Button>
        </div>
      </section>

      <Separator />

      <section className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Form fields</CardTitle>
            <CardDescription>Label, input, switch, badge.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" placeholder="admin@example.com" />
            </div>
            <div className="flex items-center gap-2">
              <Switch id="live" />
              <Label htmlFor="live">Live refresh</Label>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge>Default</Badge>
              <Badge variant="secondary">Secondary</Badge>
              <Badge variant="success">Healthy</Badge>
              <Badge variant="warning">Partial</Badge>
              <Badge variant="destructive">Down</Badge>
              <Badge variant="muted">Muted</Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Theme preview</CardTitle>
            <CardDescription>Light / dark / system rendering.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <Sun className="h-4 w-4" /> Light
            </div>
            <div className="flex items-center gap-2">
              <Moon className="h-4 w-4" /> Dark (toggle via top-right)
            </div>
            <div
              aria-hidden
              className="gradient-brand h-12 w-full rounded-md"
              title="Primary gradient"
            />
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
